import React, { useState } from 'react';
import {
  makeStyles, mergeClasses, shorthands, tokens, createCustomFocusIndicatorStyle,
} from '@fluentui/react-components';

import { Creative } from './types';
import { Preview } from './Preview';
import { Checkmark } from './Checkmark';

const useCreativeCardStyles = makeStyles({
  root: {
    position: 'relative',
    width: '100%',
    cursor: 'pointer',
    ...shorthands.borderRadius(tokens.borderRadiusSmall),

    ...createCustomFocusIndicatorStyle({
      outlineWidth: '2px',
      outlineOffset: '3px',
    }),
  },
  rootSelected: {
    ...createCustomFocusIndicatorStyle({
      outlineWidth: '2px',
      outlineOffset: '7px',
    }),
  },
  selectIndicator: {
    position: 'absolute',
    boxSizing: 'border-box',
    top: '-4px',
    left: '-4px',
    width: 'calc(100% + 8px)',
    height: 'calc(100% + 8px)',
    ...shorthands.border('2px', 'solid', 'rgba(33, 58, 213, 1)'),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
  },
  checkmarkContainer: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    zIndex: 1,
  },
});

interface ICreativeCardProps {
  creative: Creative;
  selectionMode?: 'single' | 'multiple';
  selected: boolean;
  onToggleSelect: () => void;
}

export const CreativeCard: React.FC<ICreativeCardProps> = ({
  creative, selectionMode, selected, onToggleSelect,
}) => {
  const classes = useCreativeCardStyles();

  const [loading, setLoading] = useState(true);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (event.key !== 'Enter' && event.key !== ' ') { return; }
    onToggleSelect();
  };

  return (
    <div
      role="option"
      aria-selected={selected ? 'true' : 'false'}
      className={mergeClasses('creative-card', classes.root, selected && classes.rootSelected)}
      tabIndex={0}
      onClick={onToggleSelect}
      onKeyDown={handleKeyDown}
    >
      <Preview creative={creative} onLoad={() => setLoading(false)} />
      {selected && <div className={mergeClasses('select-indicator', classes.selectIndicator)} />}
      {!loading && !!selectionMode && (
        <div className={classes.checkmarkContainer}>
          <Checkmark
            type={selectionMode}
            selected={selected}
          />
        </div>
      )}
    </div>
  );
};
